<template>
  <div class="d-flex flex-column" style="height: 100%; min-width: 0">
    <div
      class="d-flex align-center justify-space-between white--text px-1 py-2 mb-2"
      :style="{ borderBottom: `3px solid ${accentColor}` }"
    >
      <div
        class="d-flex align-center font-weight-bold text-uppercase"
        style="font-size: 15px; letter-spacing: 0.5px"
      >
        <v-icon :color="accentColor" small class="mr-2">{{ icon }}</v-icon>
        <span>{{ title }}</span>
      </div>
      <v-chip small :color="accentColor" dark class="font-weight-bold">{{
        tickets.length
      }}</v-chip>
    </div>

    <div class="kds-column-board" ref="board">
      <transition-group
        v-if="pagedTickets.length"
        tag="div"
        ref="grid"
        name="kds-card-move"
        class="kds-col-grid"
        :class="`kds-density-${density}`"
      >
        <template v-for="ticket in pagedTickets">
          <!-- FULL: rich ticket card -->
          <v-card
            v-if="density === 'full'"
            :key="`full-${ticket.id}`"
            class="kds-col-item d-flex flex-column"
            :class="{ 'kds-urgent-ring': ticket.elapsedMinutes >= 20 }"
            color="#fdfaf3"
            rounded="lg"
            elevation="6"
          >
            <v-sheet
              :color="statusColor(ticket.state)"
              dark
              class="d-flex align-center justify-space-between px-3 py-2"
            >
              <div
                class="d-flex align-center text-uppercase font-weight-bold"
                style="font-size: 13px; letter-spacing: 0.5px"
              >
                <v-icon small class="mr-1">{{ ticket.typeIcon }}</v-icon>
                <span>{{ ticket.typeLabel }}</span>
              </div>
              <div class="font-weight-bold" style="font-size: 15px">#{{ ticket.id }}</div>
            </v-sheet>

            <div class="d-flex align-center justify-space-between px-3 pt-2">
              <span class="font-weight-bold black--text" style="font-size: 14px">{{
                ticket.guestName
              }}</span>
              <span
                class="d-flex align-center"
                :class="
                  ticket.elapsedMinutes >= 20
                    ? 'error--text font-weight-bold'
                    : 'grey--text text--darken-1'
                "
                style="font-size: 12px"
              >
                <v-icon
                  x-small
                  class="mr-1"
                  :color="ticket.elapsedMinutes >= 20 ? 'error' : 'grey darken-1'"
                  >mdi-clock-outline</v-icon
                >
                {{ ticket.elapsedMinutes }} min
              </span>
            </div>

            <div
              v-if="ticket.prepRemainingMinutes !== null"
              class="d-flex justify-end px-3 pt-1"
            >
              <v-chip
                x-small
                outlined
                :color="ticket.prepRemainingMinutes < 0 ? 'error' : 'success'"
              >
                <v-icon x-small left>mdi-timer-outline</v-icon>
                {{
                  ticket.prepRemainingMinutes >= 0
                    ? `~${ticket.prepRemainingMinutes} min`
                    : `+${-ticket.prepRemainingMinutes} min`
                }}
              </v-chip>
            </div>

            <div class="px-3 py-2 flex-grow-1 black--text">
              <div v-for="(line, idx) in ticket.items" :key="idx" class="mb-1">
                <div class="d-flex" style="gap: 8px; font-size: 14px">
                  <span class="font-weight-bold" style="min-width: 26px"
                    >{{ line.quantity }}&times;</span
                  >
                  <span>{{ line.name }}</span>
                </div>
                <div
                  v-for="(choice, choiceIdx) in line.choices"
                  :key="`${idx}-${choiceIdx}`"
                  class="d-flex grey--text text--darken-2"
                  style="gap: 8px; font-size: 13px; padding-left: 26px"
                >
                  <span style="min-width: 26px">{{ choice.quantity }}&times;</span>
                  <span>{{ choice.name }}</span>
                </div>
              </div>
            </div>

            <div class="d-flex align-center px-3 pb-3">
              <v-menu offset-y top>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    small
                    v-bind="attrs"
                    v-on="on"
                    class="font-weight-bold"
                    style="cursor: pointer"
                  >
                    {{ $t(ticket.state) }}
                    <v-icon x-small right>mdi-chevron-up</v-icon>
                  </v-chip>
                </template>
                <v-list dense>
                  <v-list-item
                    v-for="status in orderStatuses"
                    :key="status.value"
                    :disabled="ticket.state === status.value"
                    @click="$emit('change-status', ticket, status.value)"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon small :color="statusColor(status.value)">{{
                        status.icon
                      }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ $t(status.value) }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>

              <v-spacer />

              <v-btn
                v-if="ticket.nextStatus"
                color="success"
                depressed
                :loading="changingId === ticket.id"
                :disabled="changingId === ticket.id"
                @click="
                  ticket.nextStatus === 'accepted'
                    ? openPrepTimeDialog(ticket)
                    : $emit('advance', ticket)
                "
              >
                <v-icon small left>mdi-check-bold</v-icon>
                {{ nextStatusLabel(ticket.nextStatus) }}
              </v-btn>
            </div>
          </v-card>

          <!-- COMPACT: single-line summary row -->
          <v-sheet
            v-else-if="density === 'compact'"
            :key="`compact-${ticket.id}`"
            dark
            rounded
            class="kds-col-item d-flex align-center px-2 py-2"
            :style="{
              background: '#1e2338',
              border: `1px solid ${ticket.elapsedMinutes >= 20 ? '#e53935' : '#2a2f42'}`,
              cursor: 'pointer',
              gap: '6px',
            }"
            @click="$emit('open-details', ticket)"
          >
            <v-chip x-small :color="accentColor" dark class="font-weight-bold"
              >#{{ ticket.id }}</v-chip
            >
            <v-icon small color="grey lighten-1">{{ ticket.typeIcon }}</v-icon>
            <span
              class="flex-grow-1 text-truncate font-weight-medium"
              style="font-size: 13px"
              >{{ ticket.guestShort }}</span
            >
            <span
              class="d-flex align-center grey--text text--lighten-1"
              style="font-size: 12px; white-space: nowrap"
            >
              <v-icon x-small class="mr-1">mdi-clock-outline</v-icon
              >{{ ticket.elapsedMinutes }} min
            </span>

            <v-btn
              v-if="ticket.nextStatus"
              fab
              x-small
              dark
              color="success"
              :loading="changingId === ticket.id"
              @click.stop="
                ticket.nextStatus === 'accepted'
                  ? openPrepTimeDialog(ticket)
                  : $emit('advance', ticket)
              "
            >
              <v-icon small>mdi-check-bold</v-icon>
            </v-btn>
          </v-sheet>

          <!-- MINIMAL: number-only chip -->
          <v-sheet
            v-else
            :key="`mini-${ticket.id}`"
            rounded
            class="kds-col-item d-flex flex-column align-center justify-center py-2"
            :style="{
              background: '#1e2338',
              border: `2px solid ${accentColor}`,
              cursor: 'pointer',
            }"
            @click="$emit('open-details', ticket)"
          >
            <span class="white--text font-weight-bold" style="font-size: 18px"
              >#{{ ticket.id }}</span
            >
            <span class="grey--text text--lighten-1" style="font-size: 11px"
              >{{ ticket.elapsedMinutes }}&prime;</span
            >
          </v-sheet>
        </template>
      </transition-group>

      <div
        v-else
        class="d-flex flex-column align-center justify-center grey--text"
        style="height: 100%; gap: 8px"
      >
        <v-icon size="56" color="grey darken-2">mdi-tray-remove</v-icon>
        <p class="font-weight-medium mb-0" style="font-size: 13px">
          {{ $t("no_active_orders") }}
        </p>
      </div>
    </div>

    <v-pagination
      v-if="totalPages > 1"
      v-model="currentPageDisplay"
      :length="totalPages"
      :total-visible="5"
      color="primary"
      dark
      circle
      class="mt-1"
    />

    <standard-dialog
      v-model="prepTimeDialog"
      :title="$t('kds_prep_time_question')"
      icon="mdi-timer-outline"
      max-width="420"
      hide-actions
    >
      <v-btn
        x-large
        block
        depressed
        color="primary"
        dark
        class="mb-4"
        @click="markReadyImmediately"
      >
        <v-icon left>mdi-lightning-bolt</v-icon>
        {{ $t("kds_ready_immediately") }}
      </v-btn>
      <v-btn
        v-for="minutes in prepTimeOptions"
        :key="minutes"
        depressed
        x-large
        block
        color="success"
        dark
        class="mb-4"
        @click="selectPrepTime(minutes)"
      >
        {{ minutes }} min
      </v-btn>
    </standard-dialog>
  </div>
</template>

<script>
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import { STATUS_COLORS, ORDER_STATUSES, KDS_PREP_TIME_OPTIONS } from "./kdsConstants";

export default {
  name: "KdsColumn",
  components: { StandardDialog },
  props: {
    title: { type: String, required: true },
    icon: { type: String, required: true },
    accentColor: { type: String, required: true },
    tickets: { type: Array, default: () => [] },
    density: { type: String, default: "full" },
    changingId: { type: [Number, String], default: null },
  },
  data() {
    return {
      currentPage: 0,
      perPage: 8,
      resizeDebounce: null,
      rotationTimer: null,
      prepTimeDialog: false,
      prepTimeTicket: null,
    };
  },
  computed: {
    orderStatuses() {
      return ORDER_STATUSES;
    },
    prepTimeOptions() {
      return KDS_PREP_TIME_OPTIONS;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.tickets.length / this.perPage));
    },
    pagedTickets() {
      const start = this.currentPage * this.perPage;
      return this.tickets.slice(start, start + this.perPage);
    },
    currentPageDisplay: {
      get() {
        return this.currentPage + 1;
      },
      set(value) {
        this.currentPage = value - 1;
      },
    },
  },
  watch: {
    tickets() {
      if (this.currentPage >= this.totalPages) {
        this.currentPage = 0;
      }
      this.$nextTick(() => this.fitToScreen());
    },
    density() {
      this.currentPage = 0;
      this.$nextTick(() => this.fitToScreen());
    },
  },
  mounted() {
    this.$nextTick(() => this.fitToScreen());
    window.addEventListener("resize", this.handleResize);
    this.rotationTimer = setInterval(this.autoAdvancePage, 9000);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    clearTimeout(this.resizeDebounce);
    clearInterval(this.rotationTimer);
  },
  methods: {
    statusColor(status) {
      return STATUS_COLORS[status] || "#757575";
    },
    nextStatusLabel(status) {
      if (status === "accepted") return this.$t("accept_order");
      return this.$t(status);
    },
    openPrepTimeDialog(ticket) {
      this.prepTimeTicket = ticket;
      this.prepTimeDialog = true;
    },
    selectPrepTime(minutes) {
      this.$emit("advance", this.prepTimeTicket, minutes);
      this.prepTimeDialog = false;
      this.prepTimeTicket = null;
    },
    markReadyImmediately() {
      this.$emit("change-status", this.prepTimeTicket, "ready");
      this.prepTimeDialog = false;
      this.prepTimeTicket = null;
    },
    autoAdvancePage() {
      if (this.totalPages <= 1) return;
      this.currentPage = (this.currentPage + 1) % this.totalPages;
    },
    fitToScreen() {
      const board = this.$refs.board;
      const grid = this.$refs.grid ? this.$refs.grid.$el || this.$refs.grid : null;
      if (!board || !grid) return;

      const sample = grid.querySelector(".kds-col-item");
      if (!sample) return;

      const gridStyle = getComputedStyle(grid);
      const colCount =
        gridStyle.gridTemplateColumns.split(" ").filter(Boolean).length || 1;
      const rowGap = parseFloat(gridStyle.rowGap) || 0;

      const boardRect = board.getBoundingClientRect();
      const cardHeight = sample.offsetHeight + rowGap;
      const rows = Math.max(1, Math.floor(boardRect.height / cardHeight));

      const nextPerPage = Math.max(colCount, colCount * rows);
      if (nextPerPage !== this.perPage) {
        this.perPage = nextPerPage;
      }
    },
    handleResize() {
      clearTimeout(this.resizeDebounce);
      this.resizeDebounce = setTimeout(() => this.fitToScreen(), 200);
    },
  },
};
</script>

<style scoped>
.kds-column-board {
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.kds-col-grid {
  display: grid;
  gap: 12px;
  align-content: start;
}

.kds-card-move-move {
  transition: transform 0.4s ease;
}

.kds-card-move-enter-active,
.kds-card-move-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.kds-card-move-enter,
.kds-card-move-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.kds-density-full,
.kds-density-compact {
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
}

.kds-density-minimal {
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
}
</style>
